import { getUserByEmail, getUserByEmailExists } from '@adornis/buchungsheld-db/api/users.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import '@adornis/forms/x-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { html } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { adminCreateUser } from '../../../api/admin-users.js';
import { addUserToCompany } from '../../../api/company.js';
import { MatchingWeekRole } from '../../../db/enums/roles.js';
import { isValidSingleEmail } from '../../../shared/email.js';
import { formatFormError } from '../../components/format-form-error.js';
import { labeledField } from '../../components/labeled-field.js';
import { adminRouter } from './admin-routes.js';

export const AdminCreateCompanyUser = stateful((props: { companyID: string }) => {
  // steps: enter email -> existing user confirm OR create new with first/last name
  const [step, setStep] = useState<'enterEmail' | 'confirmExisting' | 'createNew'>('enterEmail');
  const [email, setEmail] = useState('');
  const [existingUser, setExistingUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailValid, setEmailValid] = useState(false);

  const validateEmailString = (value: string) => {
    setEmailValid(isValidSingleEmail(value));
  };

  const checkEmail = async () => {
    setError(null);
    const mail = email.trim();
    if (!mail) return;
    setLoading(true);
    try {
      const exists = await getUserByEmailExists(mail)();
      if (exists) {
        const user = await getUserByEmail(mail)({ _id: 1, email: 1, firstName: 1, lastName: 1 });
        setExistingUser(user ?? null);
        setStep('confirmExisting');
      } else {
        setStep('createNew');
      }
    } catch (e) {
      console.error(e);
      setError(m.admin_create_company_user_check_email_error());
    } finally {
      setLoading(false);
    }
  };

  // Stabilize the newUser reference — useGeneratedForm memoizes the document
  // by `[documentInput]`, so a fresh `new User(...)` each render would reset
  // the form state. Re-create only when the email step changes.
  const [newUser, setNewUser] = useState(new User({ email: email.trim(), username: email.trim() }));
  useEffect(() => {
    setNewUser(new User({ email: email.trim(), username: email.trim() }));
  }, [email]);
  const { form, renderField } = useGeneratedForm(
    newUser,
    { translationPrefix: 'base_user_form_', validator: null, disabledFields: { email: 1 } },
    [email],
  );

  const handleCreate = async () => {
    setError(null);
    setLoading(true);
    try {
      const validationError = await form.validate();
      if (validationError) {
        setError(formatFormError(validationError, 'base_user_form_'));
        return;
      }
      // Read from form.document — `newUser` is a fresh instance per render and
      // doesn't see values picked through the form (those mutate the instance
      // captured by useGeneratedForm).
      const entity = form.document;
      // Use `adminCreateUser` (not the generic `registerUser`) because the
      // latter's wire type is AdornisUser, which doesn't declare
      // firstName/lastName — those subclass fields get stripped during
      // serialization and the user lands in the DB without a real name, so
      // the welcome mail greeting falls back to the email-derived username.
      // `adminCreateUser` takes them as separate string params and constructs
      // the User entity server-side.
      const userID = await adminCreateUser(
        (entity.email ?? '').trim(),
        '',
        (entity.firstName ?? '').trim(),
        (entity.lastName ?? '').trim(),
        MatchingWeekRole.COMPANY,
        props.companyID,
      )();
      if (!userID) {
        setError(m.admin_create_company_user_create_failed());
        return;
      }
      adminRouter.goTo('companyDetail', { params: { companyID: props.companyID } });
    } catch (e) {
      console.error(e);
      setError(m.admin_create_company_user_create_error());
    } finally {
      setLoading(false);
    }
  };

  return html`
    ${existingUser}
    <div class="p-8">
      <div class="flex justify-between items-center mb-6">
        <button
          @click=${() => adminRouter.goTo('companyDetail', { params: { companyID: props.companyID } })}
          class="btn btn-ghost"
        >
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.admin_create_company_user_back_to_company()}
        </button>
        <h1 class="text-3xl font-bold text-base-content">${m.admin_create_company_user_title()}</h1>
      </div>
      <div class="card bg-base-100 shadow">
        <div class="card-body gap-4">
          ${error ? html`<div class="alert alert-error text-error-content">${error}</div>` : null}
          ${
            step === 'enterEmail'
              ? html`
                  <div class="form-control" @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && checkEmail()}>
                    ${labeledField(
                      m.base_user_form_email_label(),
                      TextInput({
                        value: email,
                        placeholder: 'user@example.com',
                        type: 'email',
                        onChanged: value => validateEmailString(value ?? ''),
                        onPicked: value => setEmail(value ?? ''),
                      }),
                    )}
                  </div>
                  <div class="card-actions justify-end mt-2">
                    <button
                      @click=${() => adminRouter.goTo('companyDetail', { params: { companyID: props.companyID } })}
                      class="btn btn-ghost"
                    >
                      ${m.cancel()}
                    </button>
                    <button class="btn btn-primary" ?disabled=${loading || !emailValid} @click=${checkEmail}>
                      ${
                        loading
                          ? html`<span class="loading loading-spinner"></span>`
                          : html`<span class="icon-[material-symbols--search]"></span>`
                      }
                      ${m.admin_create_company_user_check_email()}
                    </button>
                  </div>
                `
              : null
          }
          ${
            step === 'confirmExisting' && existingUser
              ? html`
                  <div class="card bg-base-200">
                    <div class="card-body">
                      <div class="flex items-center gap-4">
                        <div class="avatar placeholder">
                          <div class="bg-primary text-primary-content rounded-full w-12">
                            <span class="text-lg font-bold">
                              ${(existingUser.firstName?.[0] ?? '?').toUpperCase()}${(
                                existingUser.lastName?.[0] ?? '?'
                              ).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div class="min-w-0">
                          <div class="text-xl font-semibold truncate">
                            ${existingUser.firstName} ${existingUser.lastName}
                          </div>
                          <div class="mt-1">
                            <span class="badge badge-outline">${existingUser.email}</span>
                          </div>
                        </div>
                      </div>
                      <div class="mt-4 text-sm opacity-80">${m.admin_create_company_user_already_exists_message()}</div>
                      <div class="card-actions justify-between mt-2">
                        <div class="flex gap-2">
                          <button class="btn btn-outline" @click=${() => setStep('enterEmail')}>
                            <span class="icon-[material-symbols--edit]"></span>
                            ${m.admin_create_company_user_change_email()}
                          </button>
                        </div>
                        <div class="flex gap-2">
                          <button
                            @click=${() => adminRouter.goTo('companyDetail', { params: { companyID: props.companyID } })}
                            class="btn btn-ghost"
                          >
                            ${m.cancel()}
                          </button>
                          <button
                            class="btn btn-primary"
                            ?disabled=${loading}
                            @click=${async () => {
                              if (!existingUser?._id) return;
                              setLoading(true);
                              try {
                                await addUserToCompany(existingUser._id, props.companyID)();
                                adminRouter.goTo('companyDetail', { params: { companyID: props.companyID } });
                              } catch (e) {
                                console.error(e);
                                setError(m.admin_create_company_user_add_to_company_failed());
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            <span class="icon-[material-symbols--person-add]"></span>
                            ${m.admin_create_company_user_add_to_company()}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                `
              : null
          }
          ${
            step === 'createNew'
              ? html`
                  <div class="grid gap-4" @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && handleCreate()}>
                    ${labeledField(m.base_user_form_email_label(), renderField(['email']))}
                    ${labeledField(m.base_user_form_firstName_label(), renderField(['firstName']))}
                    ${labeledField(m.base_user_form_lastName_label(), renderField(['lastName']))}
                  </div>
                  <div class="card-actions justify-between mt-4">
                    <div class="flex gap-2">
                      <button class="btn btn-outline" @click=${() => setStep('enterEmail')}>
                        <span class="icon-[material-symbols--edit]"></span>
                        ${m.admin_create_company_user_change_email()}
                      </button>
                    </div>
                    <div class="flex gap-2">
                      <button
                        @click=${() => adminRouter.goTo('companyDetail', { params: { companyID: props.companyID } })}
                        class="btn btn-ghost"
                      >
                        ${m.cancel()}
                      </button>
                      <button class="btn btn-primary" ?disabled=${loading} @click=${handleCreate}>
                        ${
                          loading
                            ? html`<span class="loading loading-spinner"></span>`
                            : html`<span class="icon-[material-symbols--add]"></span>`
                        }
                        ${m.create_user()}
                      </button>
                    </div>
                  </div>
                `
              : null
          }
        </div>
      </div>
    </div>
  `;
});
